import { Loader2, Sparkles, Gift, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calculator } from "lucide-react";
import { AdditionalEmails } from "@/components/AdditionalEmails";
import { useCustomerLevels } from "@/hooks/useCustomerLevels";

export const ProfileTab = ({
  loading, isAddMode, formData, customer, saving,
  handleFormChange, handleSaveProfile, onClose,
  handleGetPromotionSuggestions,
  setCorrectBalanceAmount, setCorrectBalanceReason, setCorrectBalanceModalOpen,
}) => {
  const { list: customerLevels } = useCustomerLevels();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* AI Promotion Suggestions Button */}
      {!isAddMode && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-full">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-purple-800">AI Promotion Suggestions</h4>
                <p className="text-sm text-purple-600">
                  Get personalized product recommendations based on purchase history
                </p>
              </div>
            </div>
            <Button
              onClick={handleGetPromotionSuggestions}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="get-promotion-suggestions-btn"
            >
              <Gift className="h-4 w-4 mr-2" />
              Suggest Promotions
            </Button>
          </div>
        </div>
      )}

      {/* Profile Section */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-700 border-b pb-2">Profile Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Name *</Label>
            <Input value={formData.name || ""} onChange={(e) => handleFormChange("name", e.target.value)} />
          </div>
          <div>
            <Label>Company</Label>
            <Input value={formData.company || ""} onChange={(e) => handleFormChange("company", e.target.value)} />
          </div>
          <div>
            <Label>Contact Number</Label>
            <Input value={formData.contact_number || ""} onChange={(e) => handleFormChange("contact_number", e.target.value)} />
          </div>
          <div>
            <Label>WhatsApp Number</Label>
            <Input value={formData.whatsapp_number || ""} onChange={(e) => handleFormChange("whatsapp_number", e.target.value)} placeholder="e.g. +27 82 123 4567" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={formData.email || ""} onChange={(e) => handleFormChange("email", e.target.value)} />
          </div>
          <div>
            <Label>Birthday</Label>
            <div className="flex gap-2">
              <select
                value={formData.birthday ? formData.birthday.substring(formData.birthday.length === 5 ? 0 : 5, formData.birthday.length === 5 ? 2 : 7) : ""}
                onChange={(e) => {
                  const month = e.target.value;
                  const currentDay = formData.birthday ? formData.birthday.substring(formData.birthday.length === 5 ? 3 : 8) : "01";
                  handleFormChange("birthday", month ? `${month}-${currentDay}` : "");
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="customer-birthday-month"
              >
                <option value="">Month</option>
                {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                  <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                ))}
              </select>
              <select
                value={formData.birthday ? formData.birthday.substring(formData.birthday.length === 5 ? 3 : 8) : ""}
                onChange={(e) => {
                  const day = e.target.value;
                  const currentMonth = formData.birthday ? formData.birthday.substring(formData.birthday.length === 5 ? 0 : 5, formData.birthday.length === 5 ? 2 : 7) : "01";
                  if (day && currentMonth) handleFormChange("birthday", `${currentMonth}-${day}`);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="customer-birthday-day"
              >
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">For birthday reminders & wishes</p>
          </div>
          <div>
            <Label>Second Person's Name (optional)</Label>
            <Input
              value={formData.secondary_name || ""}
              onChange={(e) => handleFormChange("secondary_name", e.target.value)}
              placeholder="e.g. spouse or partner on this account"
              data-testid="customer-secondary-name"
            />
          </div>
          <div>
            <Label>Second Person's Birthday</Label>
            <div className="flex gap-2">
              <select
                value={formData.secondary_birthday ? formData.secondary_birthday.substring(formData.secondary_birthday.length === 5 ? 0 : 5, formData.secondary_birthday.length === 5 ? 2 : 7) : ""}
                onChange={(e) => {
                  const month = e.target.value;
                  const currentDay = formData.secondary_birthday ? formData.secondary_birthday.substring(formData.secondary_birthday.length === 5 ? 3 : 8) : "01";
                  handleFormChange("secondary_birthday", month ? `${month}-${currentDay}` : "");
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="customer-secondary-birthday-month"
              >
                <option value="">Month</option>
                {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                  <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                ))}
              </select>
              <select
                value={formData.secondary_birthday ? formData.secondary_birthday.substring(formData.secondary_birthday.length === 5 ? 3 : 8) : ""}
                onChange={(e) => {
                  const day = e.target.value;
                  const currentMonth = formData.secondary_birthday ? formData.secondary_birthday.substring(formData.secondary_birthday.length === 5 ? 0 : 5, formData.secondary_birthday.length === 5 ? 2 : 7) : "01";
                  if (day && currentMonth) handleFormChange("secondary_birthday", `${currentMonth}-${day}`);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="customer-secondary-birthday-day"
              >
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">For accounts shared by 2 people (spouse/partner)</p>
          </div>
          <div>
            <Label>VAT Number</Label>
            <Input value={formData.vat_number || ""} onChange={(e) => handleFormChange("vat_number", e.target.value)} />
          </div>
          <div>
            <Label>Farming VAT Certificate #</Label>
            <Input
              data-testid="customer-vat-cert-input"
              value={formData.farming_vat_cert_number || ""}
              onChange={(e) => handleFormChange("farming_vat_cert_number", e.target.value)}
              placeholder="e.g. 4123456789"
            />
            <p className="text-xs text-slate-500 mt-1">Required to justify zero-rated feed / lucerne sales.</p>
          </div>
          <div>
            <Label>Certificate expiry</Label>
            <Input
              type="date"
              data-testid="customer-vat-cert-expiry-input"
              value={formData.farming_vat_cert_expiry || ""}
              onChange={(e) => handleFormChange("farming_vat_cert_expiry", e.target.value)}
            />
          </div>
          <div>
            <Label>Company Reg. No.</Label>
            <Input
              data-testid="cust-company-reg-no"
              value={formData.company_registration_number || ""}
              onChange={(e) => handleFormChange("company_registration_number", e.target.value)}
              placeholder="e.g. 2025/192647/07"
            />
            <p className="text-xs text-gray-500 mt-1">Shown on SARS invoice reprints below the customer's address</p>
          </div>
          <div>
            <Label>ID Number</Label>
            <Input value={formData.id_number || ""} onChange={(e) => handleFormChange("id_number", e.target.value)} />
          </div>
        </div>

        {/* Additional Emails */}
        {!isAddMode && customer?.id && (
          <AdditionalEmails customerId={customer.id} customerName={formData.name} primaryEmail={formData.email} />
        )}

        <div className="space-y-4 border-t pt-4">
          <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Physical Address</h4>
          <div>
            <Label>Street Address</Label>
            <Input
              data-testid="cust-street-address"
              value={formData.street_address || ""}
              onChange={(e) => handleFormChange("street_address", e.target.value)}
              placeholder="e.g. 345 CLARK STREET"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Suburb</Label>
              <Input
                data-testid="cust-suburb"
                value={formData.suburb || ""}
                onChange={(e) => handleFormChange("suburb", e.target.value)}
                placeholder="e.g. WATERKLOOF"
              />
            </div>
            <div>
              <Label>City / Town</Label>
              <Input
                data-testid="cust-city"
                value={formData.city || ""}
                onChange={(e) => handleFormChange("city", e.target.value)}
                placeholder="e.g. PRETORIA"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Postal Code</Label>
              <Input
                data-testid="cust-postal-code"
                value={formData.postal_code || ""}
                onChange={(e) => handleFormChange("postal_code", e.target.value)}
                placeholder="e.g. 0181"
              />
            </div>
            <div>
              <Label>Province</Label>
              <Input
                data-testid="cust-province"
                value={formData.province || ""}
                onChange={(e) => handleFormChange("province", e.target.value)}
                placeholder="e.g. GAUTENG"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Postal Address</h4>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid="cust-postal-same-as-physical"
                checked={!!formData.postal_same_as_physical}
                onChange={(e) => handleFormChange("postal_same_as_physical", e.target.checked)}
              />
              Same as physical address
            </label>
          </div>
          {!formData.postal_same_as_physical && (
            <>
              <div>
                <Label>P.O. Box / Postal Line</Label>
                <Input
                  data-testid="cust-po-box"
                  value={formData.po_box || ""}
                  onChange={(e) => handleFormChange("po_box", e.target.value)}
                  placeholder="e.g. PO BOX 100"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Postal Suburb</Label>
                  <Input
                    data-testid="cust-postal-suburb"
                    value={formData.postal_suburb || ""}
                    onChange={(e) => handleFormChange("postal_suburb", e.target.value)}
                    placeholder="e.g. WATERKLOOF"
                  />
                </div>
                <div>
                  <Label>Postal City / Town</Label>
                  <Input
                    data-testid="cust-postal-city"
                    value={formData.postal_city || ""}
                    onChange={(e) => handleFormChange("postal_city", e.target.value)}
                    placeholder="e.g. PRETORIA"
                  />
                </div>
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input
                  data-testid="cust-postal-code-postal"
                  value={formData.postal_code_postal || ""}
                  onChange={(e) => handleFormChange("postal_code_postal", e.target.value)}
                  placeholder="e.g. 0181"
                  className="md:max-w-xs"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Account Settings */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-700 border-b pb-2">Account Settings</h3>

        <div className="bg-gradient-to-r from-yellow-50 to-purple-50 p-4 rounded-lg">
          <Label>Customer Level</Label>
          <Select value={formData.customer_level || "Standard"} onValueChange={(v) => handleFormChange("customer_level", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {(customerLevels || []).map((lvl) => (
                <SelectItem key={lvl.name} value={lvl.name}>
                  {lvl.name === "Standard"
                    ? "Standard - No automatic discount"
                    : `${lvl.name} - ${Number(lvl.discount).toString()}% automatic discount`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">Customer level discounts are applied automatically to eligible products at checkout. Edit rates in Admin → Customer Levels.</p>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg" data-testid="customer-loyalty-type-section">
          <Label>Loyalty Cashback Type</Label>
          <Select value={formData.loyalty_customer_type || "default"} onValueChange={(v) => handleFormChange("loyalty_customer_type", v)}>
            <SelectTrigger data-testid="customer-loyalty-type-select-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default Rate (from Company Settings)</SelectItem>
              <SelectItem value="low_spend">Low spend (higher cashback)</SelectItem>
              <SelectItem value="high_spend">High spend (lower cashback)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">This setting overrides cashback % per customer type during monthly loyalty posting.</p>

          <div className="mt-3">
            <Label>Custom Cashback % (overrides type)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.loyalty_custom_rate ?? ""}
              onChange={(e) => handleFormChange("loyalty_custom_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
              placeholder="Leave blank to use Type rate"
              data-testid="customer-loyalty-custom-rate-input"
            />
            <p className="text-xs text-gray-500 mt-1">Set a fixed % for just this customer (e.g. 2.0). Leave blank to keep using the Type rate above.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Credit Limit (R)</Label>
            <Input type="number" step="0.01" value={formData.credit_limit || ""} onChange={(e) => handleFormChange("credit_limit", e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <Label>Additional Discount %</Label>
            <Input type="number" step="0.01" value={formData.discount_percent || ""} onChange={(e) => handleFormChange("discount_percent", e.target.value)} placeholder="0.00" />
            <p className="text-xs text-gray-500">Extra discount on top of level discount</p>
          </div>
        </div>

        {/* Opening Balance */}
        <div className="bg-gray-100 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-lg">Opening Balance</Label>
              <p className="text-xs text-gray-500">Adjust the imported opening balance if incorrect</p>
            </div>
            {!isAddMode && (
              <Button variant="outline" size="sm" onClick={() => { setCorrectBalanceAmount(""); setCorrectBalanceReason(""); setCorrectBalanceModalOpen(true); }} className="text-blue-600 border-blue-300 hover:bg-blue-50">
                <Calculator className="w-4 h-4 mr-1" /> Correct Balance
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold">R</span>
            <Input type="number" step="0.01" value={formData.account_balance || 0} onChange={(e) => handleFormChange("account_balance", parseFloat(e.target.value) || 0)} className="text-xl font-bold max-w-[200px]" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Positive = Customer owes you | Negative = You owe customer</p>
        </div>

        {/* Account Status */}
        <div className="space-y-3">
          <Label className="text-red-700">Account Status</Label>
          <div className={`flex items-center space-x-2 p-3 rounded ${formData.is_blocked ? 'bg-red-100 border border-red-300' : 'bg-gray-50'}`}>
            <Checkbox checked={formData.is_blocked || false} onCheckedChange={(checked) => handleFormChange("is_blocked", checked)} />
            <Label className={formData.is_blocked ? 'text-red-700' : 'text-gray-700'}>Block Account - No sales allowed</Label>
          </div>
        </div>

        {/* VAT Exemption */}
        <div className="space-y-3">
          <Label>VAT Exemption</Label>
          <div className="flex items-center space-x-2">
            <Checkbox checked={formData.has_vat_exemption || false} onCheckedChange={(checked) => handleFormChange("has_vat_exemption", checked)} />
            <Label className="font-normal">Customer has VAT Exemption Certificate</Label>
          </div>
          {formData.has_vat_exemption && (
            <Input value={formData.vat_exemption_number || ""} onChange={(e) => handleFormChange("vat_exemption_number", e.target.value)} placeholder="Enter certificate number" />
          )}
        </div>

        {/* Order Requirements */}
        <div className="space-y-3">
          <Label>Order Requirements</Label>
          <div className={`flex items-center space-x-2 p-3 rounded ${formData.requires_order_number ? 'bg-blue-50 border border-blue-300' : 'bg-gray-50'}`}>
            <Checkbox checked={formData.requires_order_number || false} onCheckedChange={(checked) => handleFormChange("requires_order_number", checked)} />
            <Label className={formData.requires_order_number ? 'text-blue-700' : 'text-gray-700'}>Requires Order Number / Reference</Label>
          </div>
        </div>

        {/* Invoice Preferences */}
        <div className="space-y-3">
          <Label>Invoice Preferences</Label>

          {/* Payment Terms — printed on every account-sale invoice PDF. */}
          <div className="space-y-1">
            <Label className="text-sm text-gray-700">Payment Terms</Label>
            <Select
              value={formData.payment_terms || "14 Days"}
              onValueChange={(v) => handleFormChange("payment_terms", v)}
            >
              <SelectTrigger data-testid="cust-payment-terms">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="COD">COD (Cash on Delivery)</SelectItem>
                <SelectItem value="7 Days">7 Days</SelectItem>
                <SelectItem value="14 Days">14 Days</SelectItem>
                <SelectItem value="30 Days">30 Days</SelectItem>
                <SelectItem value="60 Days">60 Days</SelectItem>
                <SelectItem value="90 Days">90 Days</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Printed on this customer&apos;s invoice PDFs. Default is 14 days.
            </p>
          </div>

          {/* Till Notice — pops up at the register the moment this customer
              is selected on the till. Blank = no popup. */}
          <div className="space-y-1">
            <Label className="text-sm text-gray-700 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
              Till Notice
            </Label>
            <Textarea
              value={formData.till_notice || ""}
              onChange={(e) => handleFormChange("till_notice", e.target.value)}
              placeholder="e.g. 'Please remind them to bring their OD form' or 'Only sell cash/card — do not put on account'"
              rows={3}
              maxLength={500}
              data-testid="cust-till-notice"
            />
            <p className="text-xs text-gray-500">
              Pops up at the till the moment the cashier picks this customer.
              Leave blank for no popup.
            </p>
          </div>

          {/* Staff flag — grants this customer any per-product staff
              discount configured on the Product Master. Silently
              replaces promo / loyalty / level discount for those lines. */}
          <div className={`flex items-center space-x-2 p-3 rounded ${formData.is_staff ? 'bg-indigo-50 border border-indigo-300' : 'bg-gray-50'}`}>
            <Checkbox
              checked={formData.is_staff || false}
              onCheckedChange={(checked) => handleFormChange("is_staff", checked)}
              data-testid="is-staff-checkbox"
            />
            <div>
              <Label className={formData.is_staff ? 'text-indigo-700' : 'text-gray-700'}>Staff Customer</Label>
              <p className="text-xs text-gray-500">Applies per-product Staff Discount % automatically at the till.</p>
            </div>
          </div>
          {/* Split Groceries Invoice checkbox removed — VITEX-branded split
              invoices have been retired. */}
          {/* "Auto VAT Invoice" checkbox removed — Bosveld now has its own
              VAT number, so every till slip is a valid tax invoice by
              default. The auto_vat_invoice field is preserved on the customer
              document for historical continuity but is no longer editable. */}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSaveProfile} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {isAddMode ? "Create Customer" : "Save Changes"}
        </Button>
      </div>
    </>
  );
};
